import {
  PlacementId,
  fromPlacementId,
} from '../../../../../containers/EmbroideryContainer/combination-rules/placementIds'
import { EmbroideryTypeKey } from '../../../../../containers/EmbroideryContainer/combination-rules/types'
import {
  EmbroideryItem,
  IconItemValues,
  TextItemValues,
} from '../../../../../containers/EmbroideryContainer/providers/types'
import { getFlatlayImageOffset } from '../../../../../containers/EmbroideryContainer/utils/getFlatlayImageOffset'
import { EmbroiderableProductFlatlayImage } from '../../../../../types/graphql/product'

export type LogoMarkValues = {
  /** Missing until the customer picks a file, which draws the dashed placeholder instead. */
  imageSrc: string | undefined
  isBlackAndWhite: boolean
}

type MarkFields = { key: string; placementId: PlacementId }

export type TextPreviewMark = MarkFields & { typeKey: 'text'; values: TextItemValues }

/** An icon or a logo: the graphic half of a placement, drawn above any text sharing it. */
export type GraphicPreviewMark = MarkFields &
  ({ typeKey: 'icon'; values: IconItemValues } | { typeKey: 'logo'; values: LogoMarkValues })

/**
 * One thing drawn on the preview garment. Saved items and an editor's unsaved input both become
 * marks, so the preview never needs to know which is which.
 */
export type PreviewMark = TextPreviewMark | GraphicPreviewMark

/** Everything drawn at one placement. The rules allow at most one text and one graphic there. */
export type PreviewPlacement = {
  placementId: PlacementId
  text: TextPreviewMark | undefined
  graphic: GraphicPreviewMark | undefined
}

export type PreviewFlatlayOffset = EmbroiderableProductFlatlayImage['offsets'][number]

export function markFromItem(item: EmbroideryItem): PreviewMark {
  const base = { key: item.instanceId, placementId: item.placementId }
  switch (item.typeKey) {
    case 'text':
      return { ...base, typeKey: 'text', values: item.values }
    case 'icon':
      return { ...base, typeKey: 'icon', values: item.values }
    case 'logo':
      return {
        ...base,
        typeKey: 'logo',
        values: {
          imageSrc: item.values.logoFile?.preview,
          isBlackAndWhite: item.values.logoFile?.digitizationSetting === 'BLACK_WHITE',
        },
      }
  }
}

/**
 * The marks to draw: saved items, except that an open editor's draft replaces its type's saved
 * items wholesale. An editor knows both what is saved and what is being typed, so it is the one
 * that merges them.
 */
export function mergePreviewMarks(
  items: readonly EmbroideryItem[],
  draft: { typeKey: EmbroideryTypeKey; marks: readonly PreviewMark[] } | undefined
): PreviewMark[] {
  const fromItems = items.filter(item => item.typeKey !== draft?.typeKey).map(markFromItem)
  return [...fromItems, ...(draft?.marks ?? [])]
}

export function groupMarksByPlacement(marks: readonly PreviewMark[]): PreviewPlacement[] {
  const byPlacement = new Map<PlacementId, PreviewPlacement>()
  for (const mark of marks) {
    const group = byPlacement.get(mark.placementId) ?? {
      placementId: mark.placementId,
      text: undefined,
      graphic: undefined,
    }
    if (mark.typeKey === 'text') group.text = mark
    else group.graphic = mark
    byPlacement.set(mark.placementId, group)
  }
  return [...byPlacement.values()]
}

export function getPreviewOffset(
  flatlayImage: EmbroiderableProductFlatlayImage | null | undefined,
  placementId: PlacementId
): PreviewFlatlayOffset | undefined {
  return getFlatlayImageOffset({ flatlayImage, placement: fromPlacementId(placementId) })
}
